import React from "react";

/**
 * Range52WBar — analyst-standard 52-week range strip.
 *
 * Props:
 *   low, high: numbers (52W bounds, computed from 1Y closes server-side)
 *   price: current price
 *   lowDate, highDate: optional ISO date strings for tooltip micro-labels
 *
 * Layout: [label $low] [orange track with navy marker] [label $high]
 * The current-price marker is clamped inside the track if price falls outside
 * the range. If low === high we hide the marker and show a single value.
 */
export default function Range52WBar({ low, high, price, lowDate, highDate }) {
  const validLow = typeof low === "number" && Number.isFinite(low);
  const validHigh = typeof high === "number" && Number.isFinite(high);
  const validPrice = typeof price === "number" && Number.isFinite(price);
  if (!validLow || !validHigh) return null;

  const degenerate = low === high;
  const span = high - low;
  const rawPct = validPrice && !degenerate ? ((price - low) / span) * 100 : null;
  const pct = rawPct === null ? null : Math.max(0, Math.min(100, rawPct));
  const outOfRange =
    validPrice && (price < low - 1e-9 || price > high + 1e-9);

  const fmt = (v) => {
    if (!Number.isFinite(v)) return "—";
    if (v >= 100) return v.toFixed(2);
    return v.toFixed(4);
  };

  return (
    <div className="mt-4" data-testid="range-52w-bar">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.22em] text-[#8A8075] mb-1.5">
        <span data-testid="range-52w-eyebrow">52-week range</span>
        {rawPct !== null && (
          <span className="text-[#A79E93]" data-testid="range-52w-position">
            {outOfRange ? "OUT OF RANGE · " : ""}
            {pct.toFixed(1)}% of range
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span
          className="font-mono text-[11px] text-white whitespace-nowrap"
          title={lowDate ? `Low on ${lowDate}` : undefined}
          data-testid="range-52w-low"
        >
          ${fmt(low)}
        </span>
        <div
          className="relative flex-1 h-1.5 bg-[#3A2E22] rounded-full overflow-visible"
          data-testid="range-52w-track"
        >
          {!degenerate && pct !== null && (
            <>
              {/* Filled portion from low to current price */}
              <div
                className="absolute top-0 left-0 h-full bg-[#F26A1B]/60 rounded-full"
                style={{ width: `${pct}%` }}
                aria-hidden
              />
              {/* Marker */}
              <div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                style={{ left: `${pct}%` }}
                data-testid="range-52w-marker"
              >
                <span className="block h-3 w-3 rounded-full bg-[#F26A1B] ring-2 ring-[#14110E] shadow" />
              </div>
            </>
          )}
        </div>
        <span
          className="font-mono text-[11px] text-white whitespace-nowrap"
          title={highDate ? `High on ${highDate}` : undefined}
          data-testid="range-52w-high"
        >
          ${fmt(high)}
        </span>
      </div>
    </div>
  );
}
